import { Transaction, Selection, TextSelection } from 'prosemirror-state'
import { Node } from 'prosemirror-model'
import { TrackedChangeType, TrackedChange } from 'prose-core'
import dayjs from 'dayjs'
import { Slice } from 'prosemirror-model'
import { ReplaceStep, Mapping } from 'prosemirror-transform'
import { ourUuid } from 'utils'
import { isDeletion, isInsertion } from './utils'
import { Context } from './Types'

export function addDeletionMarks(tr: Transaction, from: number, to: number, userId: number, context?: Context) {
  const schema = tr.doc.type.schema
  const siblingDeletion = findSiblingDeletion(tr)
  const createdAt = siblingDeletion?.createdAt || dayjs().toISOString()
  const uuid = context?.uuid || siblingDeletion?.uuid || ourUuid()

  const map = new Mapping()

  tr.doc.nodesBetween(from, to, (node, pos) => {
    if (pos < from && node.isBlock) {
      return true
    }

    const start = map.map(Math.max(from, pos))
    const end = map.map(Math.min(pos + node.nodeSize, to))

    if (node.type === schema.nodes.smartLink) {
      if (isDeletion(node)) {
        return false
      }

      if (isInsertion(node)) {
        const removeStep = new ReplaceStep(start, end, Slice.empty)
        if (removeStep && !tr.maybeStep(removeStep).failed) {
          map.appendMap(removeStep.getMap())
        }
        return false
      }

      addDeletionChange(tr, node, map.map(pos), { type: TrackedChangeType.Deletion, userId, createdAt, uuid })
      return false
    }

    if (isDeletion(node)) {
      return true
    }

    if (isInsertion(node)) {
      // The content is a tracked inline insertion, so we can delete its content
      if (node.isInline) {
        const removeStep = new ReplaceStep(start, end, Slice.empty)
        if (!tr.maybeStep(removeStep).failed) {
          map.appendMap(removeStep.getMap())
        }
        return false
      }

      let removeStep
      if (node.isTextblock && to < pos + node.nodeSize) {
        const selectionBefore = Selection.findFrom(tr.doc.resolve(pos), -1)
        if (selectionBefore instanceof TextSelection) {
          removeStep = new ReplaceStep(map.map(selectionBefore.$anchor.pos), map.map(to), Slice.empty)
        }
      } else {
        removeStep = new ReplaceStep(start, end, Slice.empty)
      }

      if (removeStep && !tr.maybeStep(removeStep).failed) {
        map.appendMap(removeStep.getMap())
      }

      return true
    }

    if (node.isInline) {
      // The content is not a tracked inline insertion, so we track it as a deletion
      tr.addMark(start, end, schema.marks.deletion.create({ userId, createdAt, uuid }))
      return false
    }

    if (node.type.spec.attrs?.trackedChanges) {
      addDeletionChange(tr, node, map.map(pos), { type: TrackedChangeType.Deletion, userId, createdAt, uuid })
    }
  })

  return map
}

export const addDeletionChange = (tr: Transaction, node: Node, pos: number, trackedChange: TrackedChange) => {
  const trackedChanges = (node.attrs.trackedChanges || [])
    .filter(({ type }: TrackedChange) => ![TrackedChangeType.Insertion, TrackedChangeType.Deletion].includes(type))
    .concat(trackedChange)
  /*
   * TODO: Types for this method are not expecting null as a second argument. We
   * should fork the types definitions and update them to match our version
   * of prosemirror-transform.
   */
  tr.setNodeMarkup(
    pos,
    // @ts-ignore
    null,
    {
      ...node.attrs,
      trackedChanges,
    },
    node.marks,
  )
}

const findSiblingDeletion = (tr: Transaction) => {
  const nodeAfter = tr.doc.resolve(tr.selection.to).nodeAfter
  if (!nodeAfter) {
    return
  }

  const schema = tr.doc.type.schema
  if (nodeAfter.type === schema.nodes.smartLink) {
    return nodeAfter.attrs.trackedChanges?.find(({ type }: TrackedChange) => type === TrackedChangeType.Deletion)
  }

  return nodeAfter.marks.find((mark) => mark.type === schema.marks.deletion)?.attrs
}
